import { Store } from 'vuex';
import { AxiosResponse, AxiosInstance } from 'axios';
import moment from 'moment';

import {
  RestaurantDTO,
  Restaurant,
  RestaurantDirections,
  StoreHours,
  RestaurantAddress,
  OnlineOrdering,
  Timezone,
  Catering,
  Chipotlane,
  Sustainability,
  DefaultUtensilsType,
  Experience,
  OrderDeliveryInfo,
  CustomerDeliveryAddress,
  DeliveryApiException,
} from '@/models';
import { AppState } from '@/store';
import { MUTATIONS as RESTAURANT_MUTATIONS } from '@/store/restaurant';

import { ApiService } from '../api/api.service';
import { OrderService } from '../order/order.service';
import { UiService } from '../ui/ui.service';
import { ConfigService } from '../config/config.service';
import { Logger } from '../logger/logger';
import { GoogleAnalyticsService, AnalyticsEventCategory, AnalyticsEventAction } from '../google/google-analytics.services';
import { shouldSwitchDomain, startSwitchDomainProcess } from '@/helpers';
import { AnalyticsService } from '../analytics/analytics.service';
import { FindAChipotleService } from '../find-a-chipotle/find-a-chipotle.service';
import { countries } from '@/constants';

export enum RestaurantEmbeds {
  Addresses = 'addresses',
  Directions = 'directions',
  RealHours = 'realHours',
  Timezone = 'timezone',
  OnlineOrdering = 'onlineOrdering',
  Chipotlane = 'chipotlane',
  Sustainability = 'sustainability',
  Experience = 'experience',
}

const FULL_DETAILS_EMBEDS = Object.values(RestaurantEmbeds) as RestaurantEmbeds[];

export interface RestaurantService {
  getRestaurantByNumber(restaurantNumber: number, embeds?: RestaurantEmbeds[]): Promise<Restaurant>;
  selectRestaurant(restaurant: Restaurant | null, forPickup: boolean): Promise<any>;
  refreshFavoriteRestaurantData(): Promise<void>;
  removeFavoriteById(favoriteToDropId: number): void;
  restaurantSearch(location: google.maps.LatLng, searchRadius?: number): Promise<Restaurant[]>;
  clearSearchResults(): void;
  refreshMostRecentRestaurant(): Promise<void>;
  isRestaurantOpen(restaurant: Restaurant | undefined): boolean;
  isRestaurantOpenForPickup(restaurant: Restaurant): boolean;
  getFormattedRestaurantHours(restaurant: Restaurant): string[] | undefined;
  setGroupOrderRestaurant(restaurant: Restaurant | null): void;
  setPreGroupOrderFlowRestaurant(restaurant: Restaurant | null): void;
  refreshRestaurantExperience(recentRestaurantId: number): Promise<void>;
}

export class AppRestaurantService extends ApiService implements RestaurantService {
  constructor(private orderService: OrderService, private googleAnalyticsService: GoogleAnalyticsService, private uiService: UiService, private configService: ConfigService, store: Store<AppState>, httpClient: AxiosInstance, logger: Logger, protected analytics: AnalyticsService, private findAChipotleService: FindAChipotleService) {
    super(store, httpClient, logger, analytics);
  }

  async getRestaurantByNumber(restaurantNumber: number, embeds: RestaurantEmbeds[] = [RestaurantEmbeds.Addresses, RestaurantEmbeds.RealHours, RestaurantEmbeds.Experience, RestaurantEmbeds.OnlineOrdering, RestaurantEmbeds.Sustainability]): Promise<Restaurant> {
    const params = { embed: embeds.join(',') };
    let url = `/restaurant/v3/restaurant/${restaurantNumber}`;
    return this.httpClient
      .get(url, { params })
      .then((r: AxiosResponse<RestaurantDTO>) => this.transformRestaurantDTOs([r.data])[0])
      .catch(e => this.logAndThrowApiError('getRestaurantByNumber', e));
  }

  async selectRestaurant(restaurant: Restaurant | null, forPickup: boolean) {
    const order = this.orderService.getCurrentOrder();
    if (order) {
      // If the restaurant ids don't match, switch the restaurant on the pending order to the selected restaurant
      if (restaurant && order.order.restaurantId != restaurant.restaurantNumber) {
        await this.orderService.changeRestaurantForOrder(order, restaurant.restaurantNumber, forPickup);
      }
    }

    if (restaurant) {
      this.googleAnalyticsService.sendCustomEvent({
        category: AnalyticsEventCategory.Restaurant,
        action: AnalyticsEventAction.RestaurantUpdate,
        label: restaurant.restaurantNumber.toString(),
      });
    }

    this.store.commit(RESTAURANT_MUTATIONS.SET_SELECTED_RESTAURANT, restaurant);

    if (shouldSwitchDomain(restaurant)) {
      return startSwitchDomainProcess(this.store.state, this.uiService, this.orderService, this.configService);
    }
  }

  // TODO-nvmjeq: when we have a get-many-restaurants this should be changed over
  async refreshFavoriteRestaurantData() {
    const favoriteIds = Object.keys(this.store.state.customer.favoriteRestaurants)
      .map(id => Number.parseInt(id, 10))
      .filter(id => !isNaN(id));
    const promises = favoriteIds.map(id => this.getRestaurantByNumber(id, FULL_DETAILS_EMBEDS));

    try {
      const restaurantDataArray = await Promise.all(promises);
      this.store.commit(RESTAURANT_MUTATIONS.SET_RESTAURANT_FAVORITES, restaurantDataArray);
    } catch (e: any) {
      this.logAndThrowApiError('refreshFavoriteRestaurantData', e);
    }
  }

  removeFavoriteById(favoriteToDropId: number) {
    const favorites = this.store.state.restaurant.restaurantFavorites;
    favorites.forEach((fav, index) => {
      if (fav.restaurantNumber === favoriteToDropId) {
        favorites.splice(index, 1);
      }
    });

    this.store.commit(RESTAURANT_MUTATIONS.SET_RESTAURANT_FAVORITES, favorites);
  }

  // chipotle APIs:
  // search chipoodles by lat lng
  // embeds for search are different format below than what is required by getRestaurantByNumber
  async restaurantSearch(location: google.maps.LatLng, searchRadius?: number) {
    const now = new Date();
    const simpleDate = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
    const radius = searchRadius ?? 80647
    const chipotleLocations = await this.httpClient
      .post('/restaurant/v3/restaurant', {
        latitude: location.lat(),
        longitude: location.lng(),
        radius: radius,
        restaurantStatuses: ['OPEN', 'LAB'],
        conceptIds: ['CMG'],
        orderBy: 'distance',
        orderByDescending: false,
        pageSize: 10,
        pageIndex: 0,
        embeds: {
          addressTypes: ['MAIN'],
          realHours: true,
          directions: true,
          catering: true,
          onlineOrdering: true,
          timezone: true,
          marketing: true,
          chipotlane: true,
          sustainability: true,
          experience: true,
        },
      })
      .then((r: any) => r.data)
      .catch(e => this.logAndThrowApiError('findAChipotle', e));

    // transform search reply for app consumption
    const transformedChipotleLocations = this.transformRestaurantDTOs(chipotleLocations.data);
    this.store.commit(RESTAURANT_MUTATIONS.SET_RESTAURANT_SEARCH_RESULTS, transformedChipotleLocations);
    return transformedChipotleLocations;
  }

  clearSearchResults() {
    this.store.commit(RESTAURANT_MUTATIONS.SET_RESTAURANT_SEARCH_RESULTS, []);
  }

  async refreshMostRecentRestaurant() {
    try {
      await this.refreshMostRecentDeliveryAddress();
    } catch (error: any) {
      this.logger.error('Error refreshing most recent delivery address.', error);
      return;
    }

    try {
      const recentRestaurantId = await this.orderService.getLastOrderedRestaurantId();
      if (recentRestaurantId === 0) {
        this.store.commit(RESTAURANT_MUTATIONS.SET_MOST_RECENT_RESTAURANT, null);
        return;
      }

      const recentRestaurant = await this.getRestaurantByNumber(recentRestaurantId, FULL_DETAILS_EMBEDS);

      this.store.commit(RESTAURANT_MUTATIONS.SET_MOST_RECENT_RESTAURANT, recentRestaurant);
    } catch (e: any) {
      this.logAndThrowApiError('refreshMostRecentRestaurant', e);
    }
  }

  async refreshMostRecentDeliveryAddress() {
    try {
      const deliveryResponse = await this.orderService.getLastOrderedDeliveryDetails();

      if (!deliveryResponse.deliveryWasRequested || deliveryResponse.restaurantId == 0) {
        //Most recent order is pickup
        return;
      }

      // If the most recent order was a delivery, refresh the most recent delivery address in the store and select in FAC
      const lastOrderId = (await this.orderService.getRecentOrders(deliveryResponse.restaurantId, 1)).at(0)?.orderId;
      if (!lastOrderId) {
        throw new Error('Could not find order ID for last delivery order');
      }

      const lastOrder = await this.orderService.getOrder(lastOrderId);
      if (!lastOrder) {
        throw new Error('Could not find order details for last delivery order');
      }

      const lastDeliveryDetails: OrderDeliveryInfo = await this.httpClient
        .get(`delivery/v2/whitelabel/delivery/${lastOrder.order.delivery?.deliveryId}`)
        .then((r: AxiosResponse<OrderDeliveryInfo>) => r.data);

      if (!lastDeliveryDetails.dropOffAddress.latitude || !lastDeliveryDetails.dropOffAddress.longitude) {
        //We are missing last order's latitude and longitude, which are required to show delivery address in FAC
        throw new Error('Could not find delivery address details for last delivery order');
      }

      const lastDeliveryAddress = {
        customerName: `${deliveryResponse.dropOffContactFirstName} ${deliveryResponse.dropOffContactLastName}`,
        streetAddress1: deliveryResponse.dropOffAddressLine1,
        streetAddress2: deliveryResponse.dropOffAddressLine2,
        city: deliveryResponse.dropOffAddressCity,
        state: deliveryResponse.dropOffAddressState,
        postalCode: deliveryResponse.dropOffAddressZipCode,
        countryCode: this.store.state.ui.countryProfile.countryCode,
        latitude: lastDeliveryDetails.dropOffAddress.latitude,
        longitude: lastDeliveryDetails.dropOffAddress.longitude,
        contactlessDropoff: deliveryResponse.isContactlessDelivery,
      } as CustomerDeliveryAddress;

      //Validate delivery address before setting it in the store and FAC
      const validationRequest = this.findAChipotleService.customerDeliveryAddressToValidationRequest(lastDeliveryAddress);
      const validationResult = await this.orderService.validateDeliveryAddress(validationRequest);

      if (!validationResult || validationResult instanceof DeliveryApiException || validationResult.status !== 200) {
        throw new Error('Last delivery address failed validation');
      }

      await this.orderService.setDeliveryAddress(lastDeliveryAddress, !this.store.state.restaurant.selectedRestaurant);
    } catch (error: any) {
      this.logAndThrowApiError('refreshMostRecentDeliveryAddress', error);
    }
  }

  async refreshRestaurantExperience(recentRestaurantId: number) {
    try {
      const recentRestaurant = await this.getRestaurantByNumber(recentRestaurantId, [RestaurantEmbeds.Experience]);

      this.store.commit(RESTAURANT_MUTATIONS.SET_ORDER_CANCELLATION_ENABLED, recentRestaurant.orderCancellationEnabled);
    } catch (e: any) {
      this.logAndThrowApiError('refreshRestaurantExperience', e);

      throw e;
    }
  }

  // checks actual restaurant hours
  isRestaurantOpen(restaurant: Restaurant) {
    const utcHours = this.getRestaurantUtcHours(restaurant);
    return utcHours ? moment().isAfter(utcHours.openTime) && moment().isBefore(utcHours.closeTime) : false;
  }

  // a restaurant is open for pickup orders any time from midnight until close
  isRestaurantOpenForPickup(restaurant: Restaurant): boolean {
    const utcHours = this.getRestaurantUtcHours(restaurant);
    const isBeforeClose = utcHours ? moment().isBefore(utcHours.closeTime) : false;
    // Check countries constants to see if restaurant is in service area.
    const isInServiceArea = !!restaurant.address.countryCode && !!countries[restaurant.address.countryCode.toLocaleLowerCase()];
    return restaurant.onlineOrderingEnabled && isInServiceArea && isBeforeClose;
  }

  getFormattedRestaurantHours(restaurant: Restaurant): string[] | undefined {
    if (!restaurant || !restaurant.hours || !restaurant.hours.length) {
      return [];
    }

    const dayOfWeekMetadata = {
      Monday: { rank: 0, abbreviation: 'Mon' },
      Tuesday: { rank: 1, abbreviation: 'Tue' },
      Wednesday: { rank: 2, abbreviation: 'Wed' },
      Thursday: { rank: 3, abbreviation: 'Thu' },
      Friday: { rank: 4, abbreviation: 'Fri' },
      Saturday: { rank: 5, abbreviation: 'Sat' },
      Sunday: { rank: 6, abbreviation: 'Sun' },
    };

    const mappedSortedHours: { abbreviation: string; openDateTime: string; closeDateTime: string }[] = restaurant.hours
      .map(day => Object.assign({}, day, (dayOfWeekMetadata as any)[day.dayOfWeek]))
      .sort((day1, day2) => (day1.rank > day2.rank ? 1 : -1));

    if (!(mappedSortedHours[0] && mappedSortedHours[0].abbreviation)) {
      return;
    }

    const groupedHourRanges = [
      {
        startDay: mappedSortedHours[0].abbreviation,
        endDay: '',
        openTime: mappedSortedHours[0].openDateTime,
        closeTime: mappedSortedHours[0].closeDateTime,
      },
    ];

    for (let i = 1; i < mappedSortedHours.length; i++) {
      if (
        (mappedSortedHours[i].openDateTime &&
          mappedSortedHours[i - 1].openDateTime &&
          mappedSortedHours[i].openDateTime.split('T')[1] === mappedSortedHours[i - 1].openDateTime.split('T')[1] &&
          mappedSortedHours[i].closeDateTime.split('T')[1] === mappedSortedHours[i - 1].closeDateTime.split('T')[1]) ||
        (!mappedSortedHours[i].openDateTime && !mappedSortedHours[i - 1].openDateTime)
      ) {
        // if hours = previous item hours, group days
        groupedHourRanges[groupedHourRanges.length - 1].endDay = mappedSortedHours[i].abbreviation;
      } else {
        groupedHourRanges.push({
          startDay: mappedSortedHours[i].abbreviation,
          endDay: '',
          openTime: mappedSortedHours[i].openDateTime,
          closeTime: mappedSortedHours[i].closeDateTime,
        });
      }
    }

    const formattedRanges: string[] = [];
    for (const storeHour of groupedHourRanges) {
      const timePart = storeHour.openTime ? `${moment(storeHour.openTime).format('hh:mm A')} - ${moment(storeHour.closeTime).format('hh:mm A')}` : 'Closed';
      if (storeHour.endDay == '') {
        formattedRanges.push(`${storeHour.startDay}: ${timePart}`);
      } else {
        formattedRanges.push(`${storeHour.startDay}-${storeHour.endDay}: ${timePart}`);
      }
    }

    return formattedRanges;
  }

  setGroupOrderRestaurant(restaurant: Restaurant | null): void {
    this.store.commit(RESTAURANT_MUTATIONS.SET_GROUP_ORDER_RESTAURANT, restaurant);
  }

  setPreGroupOrderFlowRestaurant(restaurant: Restaurant | null): void {
    this.store.commit(RESTAURANT_MUTATIONS.SET_PRE_GROUP_ORDER_FLOW_RESTAURANT, restaurant);
  }

  // #region private
  getRestaurantUtcHours(restaurant: Restaurant) {
    // find what day today is
    const currentDay = moment().utcOffset(restaurant.currentTimezoneOffset / 60).format('dddd');
    const currentDayHours = restaurant?.hours?.find(dayHours => dayHours.dayOfWeek === currentDay);

    if (!currentDayHours?.openDateTime) {
      return null;
    }
    // parse as UTC and apply the timezone offset
    let openTime = moment(currentDayHours.openDateTime + '+00:00').subtract(restaurant.currentTimezoneOffset, 'seconds');
    let closeTime = moment(currentDayHours.closeDateTime + '+00:00').subtract(restaurant.currentTimezoneOffset, 'seconds');

    // if the API is giving us next week's version of the day (not just if we're seeing a timezone thing)
    if (
      !openTime.isSame(moment(), 'day') &&
      moment()
        .add(5, 'day')
        .isBefore(openTime)
    ) {
      openTime = openTime.subtract(1, 'week');
      closeTime = closeTime.subtract(1, 'week');
    }

    return {
      openTime,
      closeTime,
    };
  }

  // For now, assume all of the below embeds instead of checking ApiResource stuff
  // addressTypes: ['MAIN'],
  // realHours: true,
  // normalHours: true,
  // directions: true,
  // catering: true,
  // onlineOrdering: true,
  // marketing: true,
  // chipotlane: true,
  // defaultUtensils: false
  // utensilsEnabled: false,
  // utensilsShow: false,
  // experience: true,
  transformRestaurantDTOs(chipotleLocations: RestaurantDTO[]): Restaurant[] {
    return chipotleLocations.map((chipotleLocation: RestaurantDTO) => {
      const addresses = chipotleLocation.addresses as RestaurantAddress[] | undefined;
      const onlineOrdering = chipotleLocation.onlineOrdering as OnlineOrdering | undefined;
      const catering = chipotleLocation.catering as Catering | undefined;
      const timezone = chipotleLocation.timezone as Timezone | undefined;
      const chipotlanePickupEnabled = chipotleLocation.chipotlane as Chipotlane;
      const sustainability = chipotleLocation.sustainability as Sustainability | undefined;
      const experience = chipotleLocation.experience as Experience;

      const transformedLocation: Restaurant = {
        restaurantName: chipotleLocation.restaurantName,
        restaurantNumber: chipotleLocation.restaurantNumber,
        address: addresses && addresses.length > 0 ? addresses[0] : {},
        directions: chipotleLocation.directions as RestaurantDirections,
        hours: chipotleLocation.realHours as StoreHours[],
        restaurantStatus: chipotleLocation.restaurantStatus,
        onlineOrderingEnabled: (onlineOrdering && onlineOrdering.onlineOrderingEnabled) || false,
        cateringEnabled: (catering && catering.cateringEnabled) || false,
        currentTimezoneOffset: (timezone && timezone.currentTimezoneOffset) || 0,
        chipotlanePickupEnabled: chipotlanePickupEnabled && chipotlanePickupEnabled.chipotlanePickupEnabled,
        utensilsEnabled: (sustainability && sustainability.utensilsDefaultState === DefaultUtensilsType.OPTOUT) || false,
        utensilsShow: (sustainability && sustainability.utensilsDefaultState !== DefaultUtensilsType.NA) || false,
        digitalKitchen: experience && experience.digitalKitchen,
        walkupWindowEnabled: experience && experience.walkupWindowEnabled,
        pickupInsideEnabled: experience && experience.pickupInsideEnabled,
        crewTipPickupEnabled: experience && experience.crewTipPickupEnabled,
        crewTipDeliveryEnabled: experience && experience.crewTipDeliveryEnabled,
        diningRoomOpen: experience?.diningRoomOpen,
        orderCancellationEnabled: experience?.orderCancellationEnabled,
      };

      return transformedLocation;
    });
  }

  // #endregion private
}
